import React, { useCallback, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import ReactSVG from 'react-svg'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { pathOr } from 'ramda'

import Imgix from '../imgix'
import PlayButton from '../../containers/play-button/details-play-button'
import { formatParagraphs } from '../../lib/content-item'
import { getTimeString, getDurationMinutesRemaining } from '../../containers/play-button/utils'
import { getDurationTextFromMinutes } from '../../lib/date-time'
import { TIME_FORMAT, PLAYBACK_INFO, MODALS } from '../../constants'
import { PLAY_BUTTON_IN_SERIES_EPISODE_SELECTOR } from '../../containers/play-button/constants'
import { getModalLocation } from '../../lib/modal'
import { navigationGa } from '../../lib/analytics/ga'
import ComingSoonBadge from '../shared/coming-soon-badge'
import dotsIcon from '../../../images/three-dot-menu.svg'
import { useIsAuthenticated } from '../../hooks/use-is-authenticated'
import styles from './episode.css'

function Episode({
  episode,
  playbackInfo,
  highlight
}) {
  const isAuthenticated = useIsAuthenticated()
  // looks like below change has caused DE displaying wrong episodic image,
  // because loading dock flow doesn't update the tile image when the episodic image is updated
  // const imageUri = pathOr('', ['tile', 'image'], episode)
  const imageUri = pathOr('', ['images', '0', 'uri'], episode)
  const playbackInfoData = playbackInfo?.playbackInfo
  const status = playbackInfoData?.status
  const inProgress = status === PLAYBACK_INFO.status.resume
  const numberMinutesRemaining = getDurationMinutesRemaining(playbackInfoData, TIME_FORMAT)
  // Get the text of numberMinutesRemaining, e.g. "30 MINS LEFT"
  const time = getTimeString(inProgress, numberMinutesRemaining)
  // Get the total duration of episode, e.g. "54 MINS"
  const duration = getDurationTextFromMinutes(episode.duration, { format: TIME_FORMAT })
  const isComingSoon = episode.upcoming || episode.isComingSoon
  const episodeDescription = formatParagraphs(episode.description || episode.summary)
  const history = useHistory()
  const location = useLocation()
  const [isExpanded, setIsExpanded] = useState(false)

  const watchUrl = useMemo(() => {
    if (isAuthenticated) {
      return `/watch/${episode.id}`
    }

    return getModalLocation(location, MODALS.qsParams.login)
  }, [episode.id, isAuthenticated, location])

  const onMoreInfoClick = useCallback((event) => {
    event.stopPropagation()
    navigationGa({ action: isExpanded ? 'LESS INFO' : 'MORE INFO' })
    setIsExpanded(previousIsExpanded => !previousIsExpanded)
  }, [isExpanded])

  const handleOnClick = useCallback(() => {
    if (!isComingSoon && watchUrl && playbackInfoData?.watchable) {
      history.push(watchUrl)
    }
  }, [history, isComingSoon, playbackInfoData, watchUrl])

  return (
    <div
      className={classNames(
        styles.episode,
        {
          [styles.highlight]: highlight,
          [styles.clickable]: !isComingSoon
        }
      )}
      id={`episode-${episode.id}`}
      onClick={handleOnClick}
    >
      <div
        className={classNames(
          styles.imageWrapper,
          { [styles.imgOverlay]: isComingSoon }
        )}
      >
        <Imgix
          className={styles.image}
          src={imageUri}
          recipe="series"
        />
        {
          isComingSoon
            ? (
              <ComingSoonBadge
                startDate={episode.start}
              />
            )
            : (
              <div // eslint-disable-line jsx-a11y/no-static-element-interactions
                className={styles.playWrapper}
              >
                <div className={styles.ctaContainer}>
                  <PlayButton
                    className={styles.smallPlay}
                    contentItem={episode}
                    contentItemId={episode.id}
                    playbackInfo={playbackInfo}
                    displayedInLocation={PLAY_BUTTON_IN_SERIES_EPISODE_SELECTOR}
                    textualButton={inProgress ? time : null}
                    inEpisodeList
                  />
                </div>
              </div>
            )
        }
      </div>
      <div className={styles.textAndMetadata}>
        <div className={styles.text}>
          <div className={styles.episodeTitle}>
            <span>
              {episode.episodeNumber}
              .
              {' '}
              {episode.title}
            </span>
          </div>

          <div className={styles.time}>
            {duration}
          </div>

          {/* Description for web and tablet view */}
          <p className={styles.description}>
            {episodeDescription}
          </p>

          {/* Expand Icon and Description for mobile view */}
          <div className={styles.moreInfoDescription}>
            <div className={styles.linkWrapper}>
              <div
                className={styles.link}
                onClick={onMoreInfoClick}
              >
                <ReactSVG
                  src={dotsIcon}
                  beforeInjection={(svg) => {
                    svg.setAttribute('class', classNames(
                      styles.linkIcon,
                      {
                        [styles.linkIconActive]: isExpanded
                      }
                    ))
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        className={classNames(
          styles.descriptionExpanded,
          { [styles.showExpanded]: isExpanded }
        )}
      >
        {episodeDescription}
      </div>
    </div>
  )
}

Episode.propTypes = {
  episode: PropTypes.shape({
    id: PropTypes.string,
    images: PropTypes.arrayOf(
      PropTypes.shape({
        uri: PropTypes.string
      })
    ),
    upcoming: PropTypes.bool,
    isComingSoon: PropTypes.bool,
    start: PropTypes.string,
    episodeNumber: PropTypes.number,
    title: PropTypes.string,
    description: PropTypes.string,
    summary: PropTypes.string,
    duration: PropTypes.number
  }).isRequired,
  playbackInfo: PropTypes.shape({
    status: PropTypes.string,
    playbackInfo: PropTypes.oneOfType([PropTypes.object])
  }),
  highlight: PropTypes.bool
}

Episode.defaultProps = {
  playbackInfo: null,
  highlight: false
}

export default Episode
